"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.channelForMasterDealer = exports.needsAccountSelection = exports.accountKey = exports.requiresAccountSelection = exports.toDealerMasterDealers = exports.toDealerAccounts = void 0;
var sameEmail = function (a, b) {
    return !!a && !!b && a.trim().toLowerCase() === b.trim().toLowerCase();
};
/**
 * Flatten a dealer's associations into the accounts they can shop as.
 *
 * Derived from data the channel context already loads, so the picker needs no
 * extra request. `GET /dealer/accounts` returns the same facts server-side and
 * stays the authority for anything that grants access — this is for display and
 * for deciding whether to ask the question.
 */
var toDealerAccounts = function (associations, email) {
    var _a;
    if (!Array.isArray(associations)) {
        return [];
    }
    var accounts = [];
    var _loop_1 = function (association) {
        var masterDealerId = ((association === null || association === void 0 ? void 0 : association.masterDealerId) || '').trim();
        if (!masterDealerId) {
            return "continue";
        }
        var matching = Array.isArray(association.masterDealer)
            ? association.masterDealer.find(function (md) { return ((md === null || md === void 0 ? void 0 : md.masterDealerId) || '').trim() === masterDealerId; })
            : undefined;
        var isPrincipal = Array.isArray(matching === null || matching === void 0 ? void 0 : matching.principals)
            ? matching.principals.some(function (p) { return sameEmail(p, email); })
            : false;
        // Channels live on the association, but fall back to the master dealer's
        // own list — some records only carry them there.
        var channels = (Array.isArray(association.channels) && association.channels.length
            ? association.channels
            : (matching === null || matching === void 0 ? void 0 : matching.channels) || [])
            .filter(function (c) { return !!c && !!c.trim(); })
            .map(function (c) { return c.trim().toUpperCase(); });
        for (var _b = 0, _c = Array.from(new Set(channels)); _b < _c.length; _b++) {
            var channel = _c[_b];
            accounts.push({
                masterDealerId: masterDealerId,
                masterDealerName: ((_a = matching === null || matching === void 0 ? void 0 : matching.name) === null || _a === void 0 ? void 0 : _a.trim()) || masterDealerId,
                channel: channel,
                isPrincipal: isPrincipal,
            });
        }
    };
    for (var _i = 0, associations_1 = associations; _i < associations_1.length; _i++) {
        var association = associations_1[_i];
        _loop_1(association);
    }
    return accounts;
};
exports.toDealerAccounts = toDealerAccounts;
/** Collapse the flattened accounts back to one entry per master dealer. */
var toDealerMasterDealers = function (associations, email) {
    var byId = new Map();
    for (var _i = 0, _a = (0, exports.toDealerAccounts)(associations, email); _i < _a.length; _i++) {
        var account = _a[_i];
        var existing = byId.get(account.masterDealerId);
        if (!existing) {
            byId.set(account.masterDealerId, {
                masterDealerId: account.masterDealerId,
                masterDealerName: account.masterDealerName,
                channels: [account.channel],
                isPrincipal: account.isPrincipal,
            });
            continue;
        }
        if (!existing.channels.includes(account.channel)) {
            existing.channels.push(account.channel);
        }
        // Principal anywhere in this master dealer counts for it.
        existing.isPrincipal = existing.isPrincipal || account.isPrincipal;
    }
    return Array.from(byId.values());
};
exports.toDealerMasterDealers = toDealerMasterDealers;
/**
 * Whether the dealer has to be asked which master dealer they are shopping
 * from.
 *
 * Counted per master dealer, never per channel. Somebody on a single master
 * dealer that happens to carry five channels has nothing to decide - asking
 * them would put five rows naming the same company in front of them.
 */
var requiresAccountSelection = function (masterDealers) { return Array.isArray(masterDealers) && masterDealers.length > 1; };
exports.requiresAccountSelection = requiresAccountSelection;
/** Stable key for one account, used for selection and React keys. */
var accountKey = function (account) {
    return "".concat(account.masterDealerId, "|").concat(account.channel);
};
exports.accountKey = accountKey;
/**
 * Whether the dealer still has to be asked which master dealer they are
 * shopping from.
 *
 * Keyed on whether the stored master dealer is actually one of THEIRS, not
 * merely on whether something is stored. Emptiness alone is not enough: the
 * selection is persisted against the email known when the app mounted, and
 * under impersonation that is the admin's rather than the dealer's - so an
 * impersonated dealer would silently inherit the admin's choice and never be
 * asked. The same holds when someone's associations change and their saved
 * master dealer no longer exists.
 *
 * The channel deliberately plays no part: it is switched freely inside the
 * store, so a stale one is repaired rather than turned into a question.
 */
var needsAccountSelection = function (masterDealers, selectedMasterDealerId) {
    if (!(0, exports.requiresAccountSelection)(masterDealers)) {
        return false;
    }
    return !masterDealers.some(function (masterDealer) { return masterDealer.masterDealerId === selectedMasterDealerId; });
};
exports.needsAccountSelection = needsAccountSelection;
/**
 * The channel to shop in once a master dealer is chosen, preferring one the
 * dealer is already in so switching master dealer does not silently move them
 * to a different channel as well.
 */
var channelForMasterDealer = function (masterDealer, preferredChannel) {
    var channels = (masterDealer === null || masterDealer === void 0 ? void 0 : masterDealer.channels) || [];
    if (!channels.length)
        return null;
    var preferred = (preferredChannel || '').trim().toUpperCase();
    return channels.includes(preferred) ? preferred : channels[0];
};
exports.channelForMasterDealer = channelForMasterDealer;
